import {
    faCheck,
    faHourglassHalf,
    faMinus,
    faQuestion,
    faRedo,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';
import { getAutomationStatus } from '../../services/AutomationsService';

export const REFRESH_MS = 10000;

const STATES = {
    met: {
        label: 'Met',
        icon: faCheck,
        badge: 'bg-success',
        bar: 'bg-success',
    },
    near: {
        label: 'Almost',
        icon: faHourglassHalf,
        badge: 'bg-warning text-dark',
        bar: 'bg-warning',
    },
    far: {
        label: 'Far',
        icon: faMinus,
        badge: 'bg-secondary',
        bar: 'bg-secondary',
    },
    unknown: {
        label: 'No data',
        icon: faQuestion,
        badge: 'bg-gray-500 text-white',
        bar: 'bg-gray-500',
    },
};

export function formatNumber(value) {
    if (value === null || value === undefined) return '-';
    const number = Number(value);
    // Crypto targets can be tiny (0.00001), so small values keep 8 decimals.
    const fixed = number.toFixed(Math.abs(number) < 1 ? 8 : 4);
    return fixed.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

export function getProgress(clause) {
    if (clause.state === 'met') return 100;
    if (clause.state === 'unknown' || clause.distancePct === null) return 0;
    return Math.max(0, Math.min(100, Math.round(100 - clause.distancePct)));
}

export function getDistanceText(clause) {
    if (clause.state !== 'near' && clause.state !== 'far') return '';
    const pct =
        clause.distancePct === null
            ? ''
            : ` (${formatNumber(clause.distancePct)}%)`;
    return `Missing ${formatNumber(clause.distanceAbs)}${pct}`;
}

function isLiteral(side) {
    return side.value !== null && side.label === String(side.value);
}

function ClauseRow({ clause }) {
    const state = STATES[clause.state] || STATES.unknown;
    const progress = getProgress(clause);

    return (
        <li className="list-group-item" data-state={clause.state}>
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                <div className="text-break">
                    <span className="fw-bold">{clause.left.label}</span>{' '}
                    {clause.op}{' '}
                    <span className="fw-bold">{clause.right.label}</span>
                </div>
                <span
                    className={`badge ${state.badge} d-inline-flex align-items-center justify-content-center gap-1`}
                >
                    <FontAwesomeIcon icon={state.icon} />
                    <span className="lh-1">{state.label}</span>
                </span>
            </div>
            <div className="small text-gray-600 mt-1">
                Now <strong>{formatNumber(clause.left.value)}</strong>
                {' | '}
                Target{' '}
                <strong>
                    {formatNumber(clause.right.value)}
                    {isLiteral(clause.right) ? '' : ` (${clause.right.label})`}
                </strong>
                {getDistanceText(clause) ? ` | ${getDistanceText(clause)}` : ''}
            </div>
            {clause.state !== 'unknown' ? (
                <div className="progress mt-2" style={{ height: '6px' }}>
                    <div
                        className={`progress-bar ${state.bar}`}
                        role="progressbar"
                        style={{ width: `${progress}%` }}
                        aria-valuenow={progress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                    ></div>
                </div>
            ) : (
                <></>
            )}
            {clause.edge === 'waiting_flip' ? (
                <div className="small text-gray-600 mt-2">
                    Already true before the last update. It fires only when it
                    turns from false to true.
                </div>
            ) : (
                <></>
            )}
        </li>
    );
}

/**
 * props:
 * - automationId
 * - status: the /automations/:id/status payload (clauses, summary, engine)
 *
 * While the modal is open the status is refreshed every REFRESH_MS.
 */
function ConditionsStatusModal(props) {
    const modalRef = useRef(null);
    const [status, setStatus] = useState(props.status);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [updatedAt, setUpdatedAt] = useState(new Date());
    const [error, setError] = useState('');

    useEffect(() => {
        setStatus(props.status);
        setUpdatedAt(new Date());
    }, [props.status]);

    useEffect(() => {
        const element = modalRef.current;
        if (!element) return undefined;
        const onShown = () => setIsOpen(true);
        const onHidden = () => setIsOpen(false);
        element.addEventListener('shown.bs.modal', onShown);
        element.addEventListener('hidden.bs.modal', onHidden);
        return () => {
            element.removeEventListener('shown.bs.modal', onShown);
            element.removeEventListener('hidden.bs.modal', onHidden);
        };
    }, []);

    function reload() {
        if (!props.automationId) return Promise.resolve();
        setIsLoading(true);
        return getAutomationStatus(props.automationId)
            .then(result => {
                setStatus(result);
                setUpdatedAt(new Date());
                setError('');
            })
            .catch(() => setError('Could not refresh the values.'))
            .finally(() => setIsLoading(false));
    }

    const reloadRef = useRef(reload);
    reloadRef.current = reload;

    useEffect(() => {
        if (!isOpen) return undefined;
        reloadRef.current();
        const timer = setInterval(() => reloadRef.current(), REFRESH_MS);
        return () => clearInterval(timer);
    }, [isOpen]);

    const clauses = (status && status.clauses) || [];
    const summary = (status && status.summary) || {
        met: 0,
        total: clauses.length,
        ready: false,
    };
    const engine = status && status.engine;

    return (
        <div
            className="modal fade"
            id="modalConditions"
            tabIndex="-1"
            role="dialog"
            ref={modalRef}
        >
            <div
                className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable"
                role="document"
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title">Conditions status</p>
                        <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                        ></button>
                    </div>
                    <div className="modal-body">
                        {clauses.length ? (
                            <p
                                className={
                                    'fw-bold ' +
                                    (summary.ready
                                        ? 'text-success'
                                        : 'text-gray-800')
                                }
                            >
                                {summary.met} of {summary.total} conditions met
                                {summary.ready
                                    ? '. All conditions are met.'
                                    : ''}
                            </p>
                        ) : (
                            <></>
                        )}
                        {engine && !engine.ok ? (
                            <div className="alert alert-warning py-2">
                                Engine stalled or Redis unreachable. These
                                values may be outdated.
                            </div>
                        ) : (
                            <></>
                        )}
                        {error ? (
                            <div className="alert alert-danger py-2">
                                {error}
                            </div>
                        ) : (
                            <></>
                        )}
                        {clauses.length ? (
                            <ul className="list-group">
                                {clauses.map((clause, index) => (
                                    <ClauseRow
                                        key={`${index}-${clause.text}`}
                                        clause={clause}
                                    />
                                ))}
                            </ul>
                        ) : (
                            <p className="text-gray-600 mb-0">
                                No conditions to evaluate.
                            </p>
                        )}
                    </div>
                    <div className="modal-footer justify-content-between">
                        <small className="text-gray-500">
                            Updated at {updatedAt.toLocaleTimeString()}.
                            Refreshes every {REFRESH_MS / 1000}s while open.
                        </small>
                        <button
                            type="button"
                            className="btn btn-gray-800 d-inline-flex align-items-center"
                            onClick={reload}
                            disabled={isLoading}
                        >
                            <FontAwesomeIcon
                                icon={faRedo}
                                className={
                                    'icon icon-xs me-2' +
                                    (isLoading ? ' fa-spin' : '')
                                }
                            />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ConditionsStatusModal;
